import { Config } from './Config';
import { ParserVars } from './Run';

// Per-session overrides of the HealthCheck warning thresholds (server
// WARNING_CONSTS). Each value is a [warn, error] band; a null stage inherits
// that stage's default (per-stage merge). undefined/null for the whole field
// means "inherit the template default".
// Mirrors common/cscore/api_types/session_health_vars_type.py (HealthThresholds).
export interface HealthThresholds {
  speed_wheel_skip_ratio_max?: [number | null, number | null] | null;
  ant_dropped_packets_frac_max?: [number | null, number | null] | null;
  speed_gps_multiple_ratio_diff?: [number | null, number | null] | null;
  cp_corr_diff?: [number | null, number | null] | null;
  wheel_circumference_m_diff?: [number | null, number | null] | null;
  yaw_offset_diff?: [number | null, number | null] | null;
}

export interface SessionHealthVars {
  version?: number;
  thresholds?: HealthThresholds | null;
}

export interface EquipmentSelected {
  frames?: string[];
  tyres?: string[];
  wheels_front?: string[];
  wheels_rear?: string[];
}

// Mirrors the server's CALIBRATION_COLUMNS / AUTOCAL_RUN_PARAMS /
// AUTOCAL_RESULT_PARAMS in server/app/api/db_types/config_type.py.
// Emitted by SessionsTypeConfigRuns.calibration_schema so the client can
// consume the authoritative set instead of maintaining duplicates.
export interface CalibrationSchema {
  calibration_columns: string[];
  autocal_run_params: string[];
  autocal_result_params: string[];
}

// Resolved (warn, error) calibration-drift tolerance bands, emitted by
// SessionsTypeConfigRuns.calibration_drift_bands (server WARNING_CONSTS merged
// with this session's session_health_vars override). The run mismatch asterisk
// is coloured against these instead of hardcoded literals. Each band is
// [warn, error]; either stage may be null (no band at that stage).
export interface CalibrationDriftBands {
  cp_corr_diff: [number | null, number | null];
  wheel_circumference_m_diff: [number | null, number | null];
  yaw_offset_diff: [number | null, number | null];
}

// A single weather measurement in a WeatherController collection.
// Humidity_x is percent (0-100); Baro_P is Pa; Wind_v is km/h.
export interface WeatherDataPointT {
  TimeAbs_t: number;
  Temp_T: number | null;
  Baro_P: number | null;
  Humidity_x: number | null;
  Wind_v: number | null;
  WindDir_A: number | null;
}

export interface WeatherSourceCollection {
  ts?: number | null;
  lat?: number | null;
  lon?: number | null;
  time_range?: string | null;
  weather_data: WeatherDataPointT[];
}

export type WeatherSourceKey =
  | 'weather_api'
  | 'local_station'
  | 'bike_computer'
  | 'probe'
  | 'manual'
  | 'probe_hybrid';

export type WeatherChannelKey = 'Temp_T' | 'Baro_P' | 'Humidity_x';

// Mirrors common/cspostp/weather/weather_controller.py WeatherController.
export interface WeatherControllerDict {
  weather_api: WeatherSourceCollection | null;
  local_station: WeatherSourceCollection | null;
  bike_computer: WeatherSourceCollection | null;
  probe: WeatherSourceCollection | null;
  manual: WeatherSourceCollection | null;
  probe_hybrid?: WeatherSourceCollection | null;
  selection?: WeatherSourceKey;
  // Per-channel requested-source overrides; a missing channel falls back
  // to the session-wide `selection`.
  channel_selection?: Partial<Record<WeatherChannelKey, WeatherSourceKey>>;
  offsets_raw?: Record<string, Partial<WeatherDataPointT>>;
}

// One sample of the server-resolved air-density curve
// (SessionGroupMap.resolved_weather). Source values are
// AtmosphericSourceEnum ints (see types/Run.ts).
export interface ResolvedWeatherSample {
  TimeAbs_t: number;
  AirDensity_d: number;
  sources: {
    temperature: number;
    barometric_pressure: number;
    humidity: number;
  };
}

export interface ResolvedWeather {
  samples: ResolvedWeatherSample[];
  channel_selection: Partial<Record<WeatherChannelKey, WeatherSourceKey>>;
  selection?: WeatherSourceKey;
}

export interface Session {
  id: string;
  configs: Config[];
  columns: Array<ColumnDefinition>;
  date: string;
  description: string;
  riders: any;
  team_id: string;
  rider_id: string;
  equipment_parameters?: string[];
  equipment_selected?: EquipmentSelected;
  analysis_type?: number;
  run_order?: string[];
  parser_vars?: ParserVars | null;
  session_health_vars?: SessionHealthVars | null;
  plotting_scales?: Record<string, number | null> | null;
  summary?: string | null;
  notes?: string | null;
  hidden?: boolean;
  calibration_schema?: CalibrationSchema;
  calibration_drift_bands?: CalibrationDriftBands;
  weather_controller_dict?: WeatherControllerDict | null;
  resolved_weather?: ResolvedWeather | null;
  utc_offset?: number;
  autocal_status?: number;
  autocal_ts?: string | null;
  autocal_error?: string | null;
  // Session-level UPLOAD source for device runs: false = file upload
  // (legacy device zip authoritative), true = chunk upload (runs created
  // from the live blob stream).
  chunk_upload?: boolean;
  // Session-level REPROCESS source, propagated to every run in the session
  // (rundata.reprocess_from_chunks): false = reprocess the stored
  // artifact, true = rebuild it from the device blobs first.
  reprocess_from_blob?: boolean;
}

// Mirrors the server AutocalStatus enum (cscore db_enums).
export const AUTOCAL_STATUS = {
  UNKNOWN: 0,
  UP_TO_DATE: 1000,
  PENDING: 2000,
  RUNNING: 3000,
  ERROR: 5000,
} as const;

interface ColumnDefinition {
  display_name: string;
  internal_name: string;
  places?: number;
  delta?: boolean;
  width?: number;
  units?: string;
  category?: string;
  session_ts?: number;
  session_dt?: string;
  utc_offset?: number;
}
